import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../Supabase";

const PLANS = [
  {
    id: "free",
    name: "Free",
    priceMonthly: "£0",
    priceYearly: "£0",
    description: "For getting started with basic invoices.",
    features: [
      "Up to 10 invoices / month",
      "Simple customer details",
      "Basic payment text",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    priceMonthly: "£4",
    priceYearly: "£40",
    description: "For businesses sending invoices every week.",
    features: [
      "Unlimited invoices",
      "Saved customers & items",
      "Payment tracking & metrics",
      "Priority support",
    ],
    popular: true,
  },
];

const TAB_KEYS = ["plans", "billing"];
const BILLING_SEGMENTS = ["monthly", "yearly"];

const SubscriptionPage = ({ onPlanUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);

  const [subscription, setSubscription] = useState(null);

  // UI-only state
  const [viewTab, setViewTab] = useState("plans");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [selectedPlanId, setSelectedPlanId] = useState("pro");

  const loadSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const mapResponseToState = (planPayload, subscriptionPayload) => {
        const planId = planPayload?.planId === "pro" ? "pro" : "free";
        const billing =
          planId === "pro"
            ? subscriptionPayload?.billingCycle === "yearly"
              ? "yearly"
              : "monthly"
            : "monthly";

        return {
          planId,
          status: subscriptionPayload?.status || "none",
          renewsAt: subscriptionPayload?.renewsAt || null,
          billingCycle: billing,
        };
      };

      const { data, error } = await supabase.functions.invoke(
        "get-plan-limits"
      );
      if (error) throw error;

      const normalized = mapResponseToState(data?.plan, data?.subscription);

      setSubscription(normalized);
      setBillingCycle(normalized.billingCycle);
      setSelectedPlanId(
        normalized.planId === "free" ? "pro" : normalized.planId
      );
      if (typeof onPlanUpdated === "function") {
        onPlanUpdated();
      }
    } catch (e) {
      console.warn(
        "Failed to load subscription via Edge Function, attempting fallback",
        e
      );

      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user?.id) {
          throw new Error("You must be signed in to manage plans.");
        }

        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("plan, subscription_status, plan_expires_at, plan_interval")
          .eq("id", user.id)
          .maybeSingle();

        if (profileError) throw profileError;

        const fallback = {
          planId: profile?.plan === "pro" ? "pro" : "free",
          status: profile?.subscription_status || "none",
          renewsAt: profile?.plan_expires_at || null,
          billingCycle:
            profile?.plan === "pro" && profile?.plan_interval === "yearly"
              ? "yearly"
              : "monthly",
        };

        setSubscription(fallback);
        setBillingCycle(fallback.billingCycle);
        setSelectedPlanId(fallback.planId === "free" ? "pro" : fallback.planId);
        if (typeof onPlanUpdated === "function") {
          onPlanUpdated();
        }
      } catch (fallbackError) {
        console.warn("Subscription fallback query failed", fallbackError);
        setError(
          fallbackError?.message || e?.message || "Failed to load subscription."
        );
      }
    } finally {
      setLoading(false);
    }
  }, [onPlanUpdated]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (status === "success") {
      setBanner("Payment successful. Your plan will update shortly.");
      loadSubscription();
    } else if (status === "cancelled") {
      setBanner("Checkout was cancelled.");
    }
    if (status || params.get("session_id")) {
      params.delete("status");
      params.delete("session_id");
      const next = params.toString();
      const nextUrl = next
        ? `${window.location.pathname}?${next}`
        : window.location.pathname;
      window.history.replaceState({}, "", nextUrl);
    }
  }, [loadSubscription]);

  const currentPlan = useMemo(() => {
    if (!subscription) return PLANS[0];
    return PLANS.find((p) => p.id === subscription.planId) || PLANS[0];
  }, [subscription]);

  const isOnPro = subscription?.planId === "pro";
  const isManagingCurrentPlan = isOnPro && selectedPlanId === "pro";

  const currentPriceLabel = useMemo(() => {
    if (!subscription) return "Not subscribed";

    const plan = PLANS.find((p) => p.id === subscription.planId);
    if (!plan) return "Not subscribed";

    const price =
      subscription.billingCycle === "monthly"
        ? plan.priceMonthly
        : plan.priceYearly;

    const suffix = subscription.billingCycle === "monthly" ? "/month" : "/year";

    return `${price} ${suffix}`;
  }, [subscription]);

  const statusBadge = useMemo(() => {
    const base = {
      label: "No active plan",
      bg: "bg-slate-100",
      text: "text-slate-700",
    };

    const rawStatus =
      typeof subscription?.status === "string" && subscription.status.length > 0
        ? subscription.status
        : "none";

    if (rawStatus === "none") return base;

    const map = {
      trialing: {
        label: "Trial active",
        bg: "bg-emerald-50",
        text: "text-emerald-700",
      },
      active: {
        label: "Active",
        bg: "bg-emerald-50",
        text: "text-emerald-700",
      },
      past_due: {
        label: "Payment issue",
        bg: "bg-amber-50",
        text: "text-amber-700",
      },
      canceled: {
        label: "Canceled",
        bg: "bg-slate-100",
        text: "text-slate-700",
      },
      incomplete: {
        label: "Payment pending",
        bg: "bg-amber-50",
        text: "text-amber-700",
      },
      incomplete_expired: {
        label: "Payment failed",
        bg: "bg-red-50",
        text: "text-red-700",
      },
      unpaid: {
        label: "Unpaid",
        bg: "bg-amber-50",
        text: "text-amber-700",
      },
    };

    const fallbackLabel = rawStatus
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
      .trim();

    return (
      map[rawStatus] || {
        ...base,
        label: fallbackLabel.length > 0 ? fallbackLabel : base.label,
      }
    );
  }, [subscription]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleBillingCycleChange = (cycle) => {
    if (cycle === billingCycle) return;
    setBillingCycle(cycle);
  };

  const startCheckout = useCallback(async () => {
    const planKey = billingCycle === "yearly" ? "pro_yearly" : "pro_monthly";
    const origin = window.location.origin;
    const successUrl = `${origin}/subscriptions/confirmed?status=success&session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${origin}/subscriptions?status=cancelled`;
    const { data, error: fnError } = await supabase.functions.invoke(
      "create-checkout",
      {
        body: { plan: planKey, successUrl, cancelUrl },
      }
    );

    if (fnError) {
      throw new Error(fnError.message || "Failed to start checkout.");
    }

    if (!data?.url) {
      throw new Error("Checkout session missing URL.");
    }
    window.location.href = data.url;
  }, [billingCycle]);

  const openPortal = useCallback(async () => {
    const returnUrl = `${window.location.origin}/subscriptions`;
    const { data, error: fnError } = await supabase.functions.invoke(
      "create-portal-session",
      {
        body: { returnUrl },
      }
    );

    if (fnError) {
      throw new Error(fnError.message || "Failed to open billing portal.");
    }
    if (!data?.url) {
      throw new Error("Portal session missing URL.");
    }
    window.location.href = data.url;
  }, []);

  const handleConfirm = async () => {
    if (!subscription) return;
    if (subscription.planId === "free" && selectedPlanId === "free") {
      setError("You are already on the Free plan.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (subscription.planId !== "pro" && selectedPlanId === "pro") {
        await startCheckout();
        return;
      }

      if (subscription.planId === "pro") {
        await openPortal();
        return;
      }
    } catch (e) {
      console.error(e);
      setError(
        e?.message ||
          "Something went wrong with your subscription. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPro = async () => {
    if (!isOnPro || !subscription) return;

    const ok = window.confirm(
      "Are you sure you want to cancel Pro? You’ll keep access until the end of your billing period."
    );
    if (!ok) return;

    try {
      setSaving(true);
      setError(null);
      await openPortal();
    } catch (e) {
      console.error(e);
      setError(
        e?.message ||
          "Failed to cancel subscription. Please refresh and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 text-sm text-slate-600 shadow-sm">
          Loading subscription…
        </div>
      </div>
    );
  }

  const tabIndex = TAB_KEYS.indexOf(viewTab);
  const billingIndex = BILLING_SEGMENTS.indexOf(billingCycle);

  return (
    <div className="flex flex-col gap-4">
      {banner && (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {banner}
        </div>
      )}
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center justify-between gap-3">
          <span>Subscription</span>
          {subscription && (
            <span className="rounded-full bg-slate-900/5 px-3 py-1 text-xs font-medium text-slate-600">
              {currentPriceLabel}
            </span>
          )}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Choose the plan that matches how often you send invoices. You can
          upgrade, downgrade, or cancel anytime.
        </p>
      </div>

      {/* Card */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {/* Top tabs */}
        <div className="mb-4 w-full max-w-xs">
          <div className="relative flex rounded-xl bg-slate-100 p-1">
            <div
              className="absolute inset-y-1 w-1/2 rounded-lg bg-white shadow-sm transition-transform"
              style={{
                transform: `translateX(${tabIndex * 100}%)`,
              }}
            />
            {TAB_KEYS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setViewTab(tab)}
                className={[
                  "relative z-10 flex-1 px-2 py-1 text-xs font-medium transition-colors",
                  viewTab === tab
                    ? "text-slate-900"
                    : "text-slate-500 hover:text-slate-700",
                ].join(" ")}
              >
                {tab === "plans" ? "Plans" : "Billing"}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {viewTab === "plans" ? (
          <div className="space-y-6">
            {/* Billing cycle selector */}
            <div>
              <div className="mb-2 ml-0.5 text-xs font-medium text-slate-700">
                Billing cycle
              </div>
              <div className="relative inline-flex w-full max-w-xs rounded-xl bg-slate-100 p-1">
                <div
                  className="absolute inset-y-1 w-1/2 rounded-lg bg-white shadow-sm transition-transform"
                  style={{
                    transform: `translateX(${billingIndex * 100}%)`,
                  }}
                />
                {BILLING_SEGMENTS.map((cycle) => (
                  <button
                    key={cycle}
                    type="button"
                    onClick={() => handleBillingCycleChange(cycle)}
                    className={[
                      "relative z-10 flex-1 px-2 py-1 text-xs font-medium transition-colors",
                      billingCycle === cycle
                        ? "text-slate-900"
                        : "text-slate-500 hover:text-slate-700",
                    ].join(" ")}
                  >
                    {cycle === "monthly" ? "Monthly" : "Yearly (save 2 months)"}
                  </button>
                ))}
              </div>
            </div>

            {/* Plans list */}
            <div>
              <div className="mb-2 ml-0.5 text-xs font-medium text-slate-700">
                Choose a plan
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {PLANS.map((plan) => {
                  const isSelected = selectedPlanId === plan.id;
                  const isCurrent = subscription?.planId === plan.id;

                  const price =
                    billingCycle === "monthly"
                      ? plan.priceMonthly
                      : plan.priceYearly;
                  const suffix =
                    billingCycle === "monthly" ? "/month" : "/year";

                  return (
                    <button
                      type="button"
                      key={plan.id}
                      onClick={() => setSelectedPlanId(plan.id)}
                      className={[
                        "flex h-full flex-col rounded-2xl border px-4 py-3.5 text-left transition-colors",
                        isSelected
                          ? "border-slate-900 bg-slate-50"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/60",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="mb-1 flex flex-wrap items-center gap-1.5">
                            <span className="text-sm font-semibold text-slate-900">
                              {plan.name}
                            </span>
                            {plan.popular && (
                              <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                Most popular
                              </span>
                            )}
                            {isCurrent && (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500">
                            {plan.description}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-base font-semibold text-slate-900">
                            {price}{" "}
                            <span className="text-xs font-normal text-slate-500">
                              {suffix}
                            </span>
                          </div>
                        </div>
                      </div>

                      <ul className="mt-3 space-y-1.5">
                        {plan.features.map((feat) => (
                          <li
                            key={feat}
                            className="flex items-center gap-2 text-[11px] text-slate-600"
                          >
                            <span
                              className={[
                                "inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px]",
                                plan.id === "pro"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-500",
                              ].join(" ")}
                            >
                              ✓
                            </span>
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="pt-2">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving}
                className={[
                  "inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors",
                  saving
                    ? "cursor-wait bg-slate-300 text-white"
                    : "bg-black text-white hover:bg-slate-900",
                ].join(" ")}
              >
                {saving
                  ? "Working…"
                  : isManagingCurrentPlan
                  ? "Manage billing"
                  : selectedPlanId === "pro"
                  ? "Continue to upgrade"
                  : "Switch to Free"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Your plan summary */}
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Your plan
              </div>
              <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex-1">
                  <div className="text-lg font-semibold text-slate-900">
                    {currentPlan.name}
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {subscription?.status === "none"
                      ? "Choose a plan to unlock more features."
                      : currentPlan.description}
                  </p>
                </div>
                <div
                  className={[
                    "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium",
                    statusBadge.bg,
                    statusBadge.text,
                  ].join(" ")}
                >
                  {statusBadge.label}
                </div>
              </div>

              {subscription?.renewsAt ? (
                <p className="mt-2 text-[11px] text-slate-500">
                  Renews on{" "}
                  <span className="font-medium text-slate-700">
                    {formatDate(subscription.renewsAt) || subscription.renewsAt}
                  </span>{" "}
                  at {currentPriceLabel}.
                </p>
              ) : (
                <p className="mt-2 text-[11px] text-slate-500">
                  No upcoming renewal – upgrade to Pro to unlock unlimited
                  invoicing and detailed metrics.
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-3">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving}
                className={[
                  "inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors",
                  saving
                    ? "cursor-wait bg-slate-300 text-white"
                    : "bg-black text-white hover:bg-slate-900",
                ].join(" ")}
              >
                {saving
                  ? "Working…"
                  : isManagingCurrentPlan
                  ? "Manage billing"
                  : selectedPlanId === "pro"
                  ? "Continue to upgrade"
                  : "Switch to Free"}
              </button>

              {isOnPro && (
                <button
                  type="button"
                  onClick={handleCancelPro}
                  disabled={saving}
                  className="inline-flex w-full items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel Pro at period end
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default SubscriptionPage;
